/**
 * NoConsoleAccess — the shell-level access page for a signed-in principal
 * without an admin role (DNKADM-0351). Rendered by `AuthGate` in place of the
 * `<Outlet/>` for the whole authenticated tree — no nav, no tenant switcher,
 * no screen content ever paints for this principal, including during load
 * (the gate decides before the shell mounts).
 *
 * This is a blank-slate page, deliberately unbranded like the other boot-level
 * screens (`SessionExpired`, `SignedOut`, `BootstrapGate`'s failure state) that
 * render outside the app chrome — it carries no tenant name, no counts, no
 * operational detail. The copy does not suggest the visitor did anything
 * wrong; they followed a link they were sent (DNKAPI-0506 fixes the one that
 * sends them here today — this page is what happens if any other path still
 * lands a non-admin in the console: a bookmark, a shared link, a future invite
 * bug, a member who was once staff).
 *
 * **Member-app link (DNKADM-0352).** Sourced from `getBootstrap().memberOrigin`
 * (`DNKAPI-0507`/`DNKAPI-0512`) — never hardcoded, never derived from this
 * console's own hostname by string surgery (that pattern already exists as
 * `env.memberOrigin`, DNKADM-0283, but it's a nullable *guess* good enough only
 * for the guest-booking preview affordance it was built for; the Operator
 * explicitly rejected reusing it here, 2026-08-31). When `memberOrigin` is
 * null, the page renders without the link — degrade, never guess a URL.
 *
 * **Sign-out (DNKADM-0352).** Clears the session exactly like the console's
 * own sign-out (`AppLayout`) — same best-effort `authApi.logout()`, same
 * local clearing — but its DESTINATION differs on purpose. The console's own
 * sign-out lands on `/auth/signed-out`, whose "Sign in again" button returns
 * here (`silentRefresh.ts`'s `redirectToCentralizedLogin`, correct for staff).
 * Sending a non-admin there would loop them straight back to this page after
 * their next sign-in — the exact bug 2026-08-31 reported: sign out, sign in,
 * land back on `admin.….journix.ai/auth/return`. So this page navigates
 * cross-origin instead: to `memberOrigin` when known, or to the bootstrapped
 * `loginUrl` with **no** `returnUrl` when it is not — a dead end beats a loop.
 */
import { useCallback, useState } from 'react';
import { Link } from '@mui/material';
import { authApi } from '@/api/auth';
import { useAuth } from '@/auth/authHooks';
import { getBootstrap } from '@/config/bootstrap';

const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '0.75rem',
  padding: '2rem',
  textAlign: 'center',
} as const;

export function NoConsoleAccess() {
  const { signOut } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const { memberOrigin, loginUrl } = getBootstrap();

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      // A failed network logout must not block clearing the local session.
    }
    signOut();
    // Never back to this console's own login — that is the loop this unit
    // fixes. Land on the member app when we know where it is; otherwise on
    // centralized login with no returnUrl at all, so nothing pulls them back.
    window.location.assign(memberOrigin ?? loginUrl);
  }, [memberOrigin, loginUrl, signOut]);

  return (
    <main role="main" style={containerStyle}>
      <h1>This console isn&rsquo;t available for your account</h1>
      <p style={{ maxWidth: '32rem' }}>
        Dink Admin is a staff tool. Your bookings and account aren&rsquo;t managed here.{' '}
        {memberOrigin ? (
          <>
            Go to the <Link href={memberOrigin}>member app</Link> to get to them instead.
          </>
        ) : (
          <>Use the link from your original invitation to get to them instead.</>
        )}
      </p>
      <button type="button" onClick={() => void handleSignOut()} disabled={signingOut}>
        {signingOut ? 'Signing out…' : 'Sign out'}
      </button>
    </main>
  );
}
